import {useTranslation} from 'react-i18next'

/**
 * Custom hook to get translations for preventive activities
 * Allows mapping English texts from the backend to the corresponding
 * translations in the user's selected language
 */
export const usePreventiveTranslations = () => {
  const {t} = useTranslation<string>()

  // Mappings of backend texts to our translated constants
  const descriptionMap: Record<string, string> = {
    'Annual Eye Test': t('Annual Eye Test'),
    'Eye Examination in Diabetes (EED)': t('Eye Examination in Diabetes (EED)'),
    'Annual Hearing Test': t('Annual Hearing Test'),
    'Annual Dental Examination': t('Annual Dental Examination'),
    'Thyroid Function Tests': t('Thyroid Function Tests'),
    'Breast Cancer preventive (BCS)': t('Breast Cancer preventive (BCS)'),
    'Breast Cancer Screening': t('Breast Cancer Screening'),
    'Annual Blood Pressure': t('Annual Blood Pressure'),
    'Annual Blood Pressure Check': t('Annual Blood Pressure Check'),
    'Controlling High Blood Pressure (CBP)': t(
      'Controlling High Blood Pressure (CBP)',
    ),
    'Blood Pressure Control for Patients With Diabetes (BPD)': t(
      'Blood Pressure Control for Patients With Diabetes (BPD)',
    ),
    Electrocardiogram: t('Electrocardiogram'),
    'Cholesterol Testing': t('Cholesterol Testing'),
    'Diabetes Screening': t('Diabetes Screening'),
    'HbA1c Control for Patients with Diabetes (HBD)': t(
      'HbA1c Control for Patients with Diabetes (HBD)',
    ),
    'Kidney Health Evaluation for Patients with Diabetes (KED)': t(
      'Kidney Health Evaluation for Patients with Diabetes (KED)',
    ),
    'Colorectal Cancer preventive (COL)': t(
      'Colorectal Cancer preventive (COL)',
    ),
    'Bowel Cancer Screening': t('Bowel Cancer Screening'),
    'Abdominal Aortic Aneurysm (AAA) Screening': t(
      'Abdominal Aortic Aneurysm (AAA) Screening',
    ),
    'Cervical Cancer preventive (CCS)': t('Cervical Cancer preventive (CCS)'),
    'Cervical Cancer Screening': t('Cervical Cancer Screening'),
    'Prostate Cancer Screening': t('Prostate Cancer Screening'),
    'Osteoporosis Screening - DXA Scan': t('Osteoporosis Screening - DXA Scan'),
    'Skin Cancer - examination / mapping / early management': t(
      'Skin Cancer - examination / mapping / early management',
    ),
    Anxiety: t('Anxiety'),
    'Major Depressive Disorder': t('Major Depressive Disorder'),
    'Weight Assessment / BMI': t('Weight Assessment / BMI'),
    Smoking: t('Smoking'),
    'Smoking - Yes/No - If Yes offer counselling on quitting': t(
      'Smoking - Yes/No - If Yes offer counselling on quitting',
    ),
    'Alcohol - Audit-C': t('Alcohol - Audit-C'),
    'Low Dose CT for Lung Cancer Screening': t(
      'Low Dose CT for Lung Cancer Screening',
    ),
    'Unhealthy Drug Use - NIDA Quick Screen': t(
      'Unhealthy Drug Use - NIDA Quick Screen',
    ),
  }

  const questionMap: Record<string, string> = {
    'Have you had an eye test in the past year?': t(
      'Have you had an eye test in the past year?',
    ),
    'Have you had an eye test in the past two years?': t(
      'Have you had an eye test in the past two years?',
    ),
    'Have you had an eye examination in the past year?': t(
      'Have you had an eye examination in the past year?',
    ),
    'Have you had a diabetic eye examination in the past year?': t(
      'Have you had a diabetic eye examination in the past year?',
    ),
    'Have you had a hearing test in the past year?': t(
      'Have you had a hearing test in the past year?',
    ),
    'Have you had a hearing test in the past two years?': t(
      'Have you had a hearing test in the past two years?',
    ),
    'Have you had a dental examination in the past year?': t(
      'Have you had a dental examination in the past year?',
    ),
    'Have you had thyroid blood tests in the past year?': t(
      'Have you had thyroid blood tests in the past year?',
    ),
    'Have you had a mammogram to check your breasts in the past two years?': t(
      'Have you had a mammogram to check your breasts in the past two years?',
    ),
    'Have you had your blood pressure recorded in the past year?': t(
      'Have you had your blood pressure recorded in the past year?',
    ),
    'Have you had your blood pressure recorde in the past 3 months?': t(
      'Have you had your blood pressure recorde in the past 3 months?',
    ),
    'Have you had an EKG in the past year?': t(
      'Have you had an EKG in the past year?',
    ),
    'Have you had your cholesterol tested in the past year?': t(
      'Have you had your cholesterol tested in the past year?',
    ),
    'Have you had a diabetes screening test in the past 3 years? This may be a blood test called "Glucose" or "HbA1c".':
      t(
        'Have you had a diabetes screening test in the past 3 years? This may be a blood test called "Glucose" or "HbA1c".',
      ),
    'Have you had a HbA1c test in the past 6 months?': t(
      'Have you had a HbA1c test in the past 6 months?',
    ),
    'Have you had a blood and urine test for kidney function in the past year? This may be called eGFR and uACR.':
      t(
        'Have you had a blood and urine test for kidney function in the past year? This may be called eGFR and uACR.',
      ),
    'Have you had a colon preventive (FIT) test within the past year? This may be a fecal examination.':
      t(
        'Have you had a colon preventive (FIT) test within the past year? This may be a fecal examination.',
      ),
    'Have you had a colon preventive (Colonoscopy) test within the past year?':
      t(
        'Have you had a colon preventive (Colonoscopy) test within the past year?',
      ),
    'Have you had a bowel screening test within the past two years?': t(
      'Have you had a bowel screening test within the past two years?',
    ),
    'Have you had a cervical screening in the past 3 years?': t(
      'Have you had a cervical screening in the past 3 years?',
    ),
    'Have you had a cervical screening in the past 5 years?': t(
      'Have you had a cervical screening in the past 5 years?',
    ),
    'Have you had a PSA test in the past year?': t(
      'Have you had a PSA test in the past year?',
    ),
    'Have you had a DEXA scan in the past year?': t(
      'Have you had a DEXA scan in the past year?',
    ),
    'Have you had a skin examination in the past year?': t(
      'Have you had a skin examination in the past year?',
    ),
    'Have you had your BMI / Weight measured in the past year?': t(
      'Have you had your BMI / Weight measured in the past year?',
    ),
    'Have you had a CT scan of your lungs in the past year?': t(
      'Have you had a CT scan of your lungs in the past year?',
    ),
  }

  /**
   * Gets the translation of a text or returns the original text as fallback
   * @param text Original text in English
   * @param mapping Mapping of texts to translations
   * @returns Translated text or the original if no translation exists
   */
  const getTranslatedText = (
    text: string | undefined,
    mapping: Record<string, string>,
  ) => {
    if (!text || text === '-') return ''
    return mapping[text] || text
  }

  return {
    getTranslatedDescription: (text: string) =>
      getTranslatedText(text, descriptionMap),
    getTranslatedQuestion: (text: string | undefined) =>
      getTranslatedText(text, questionMap),
  }
}
